import { useState, useEffect, useCallback } from 'react';

// Import constants
import { DEVICE_LINKING } from '../../constants/device-linking';

// Import utils
import { buildInitialDeviceId } from '../../utils/persistence';
import { logger } from '../../utils/logger';

// Import APIs
import {
  generateLinkCode,
  getLinkedDevices,
  linkDevices,
  unlinkDevice,
} from '../../api/devices';

export function useDeviceLinking() {
  const [deviceId, setDeviceId] = useState<string>('');
  const [linkCode, setLinkCode] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [linkCodeInput, setLinkCodeInput] = useState('');
  const [linkedDeviceIds, setLinkedDeviceIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadLinkedDevices = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      const result = await getLinkedDevices(id);
      setLinkedDeviceIds(result.linkedDeviceIds);
    } catch (err) {
      logger.error('Failed to load linked devices:', err);
      setError('Failed to load linked devices. ' + err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = buildInitialDeviceId();
    setDeviceId(id);
    loadLinkedDevices(id);
  }, [loadLinkedDevices]);

  const handleGenerateCode = useCallback(async () => {
    if (!deviceId) return;
    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await generateLinkCode(deviceId);
      setLinkCode(result.code);
      setSuccess(
        'Link code generated! Share this code with your other device.',
      );
    } catch (err) {
      logger.error('Failed to generate link code:', err);
      setError('Failed to generate link code. ' + err);
    } finally {
      setIsGenerating(false);
    }
  }, [deviceId]);

  const handleLinkDevices = useCallback(async () => {
    if (!deviceId || !linkCodeInput.trim()) {
      setError('Please enter a link code');
      return;
    }
    setIsLinking(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await linkDevices(deviceId, linkCodeInput.trim());
      setLinkedDeviceIds(result.linkedDeviceIds);
      setLinkCodeInput('');
      setSuccess(
        'Devices linked successfully! You can now access your meeting planners across devices.',
      );
      await loadLinkedDevices(deviceId);
      // Reload to refresh any room ownership checks
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      logger.error('Failed to link devices:', err);
      setError('Failed to link devices. ' + err);
    } finally {
      setIsLinking(false);
    }
  }, [deviceId, linkCodeInput, loadLinkedDevices]);

  const handleCopyCode = useCallback(() => {
    if (linkCode) {
      navigator.clipboard.writeText(linkCode);
      setCopied(true);
      setTimeout(() => setCopied(false), DEVICE_LINKING.COPY_FEEDBACK_TIMEOUT);
    }
  }, [linkCode]);

  const handleUnlinkDevice = useCallback(
    async (deviceToUnlink: string) => {
      if (!deviceId || deviceToUnlink === deviceId) return;
      setIsUnlinking(deviceToUnlink);
      setError(null);
      setSuccess(null);
      try {
        await unlinkDevice(deviceId, deviceToUnlink);
        await loadLinkedDevices(deviceId);
        setSuccess('Device unlinked successfully');
        // Reload to refresh any room ownership checks
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } catch (err) {
        logger.error('Failed to unlink device:', err);
        setError('Failed to unlink device. ' + err);
      } finally {
        setIsUnlinking(null);
      }
    },
    [deviceId, loadLinkedDevices],
  );

  const handleLinkCodeInputChange = useCallback((text: string) => {
    // Only allow digits, max 6 characters
    setLinkCodeInput(
      text.replace(/\D/g, '').slice(0, DEVICE_LINKING.CODE_LENGTH),
    );
  }, []);

  const isAtLimit = linkedDeviceIds.length >= DEVICE_LINKING.MAX_DEVICES;
  const canLink =
    linkCodeInput.length === DEVICE_LINKING.CODE_LENGTH && !isAtLimit;

  return {
    deviceId,
    linkCode,
    isGenerating,
    isLinking,
    linkCodeInput,
    linkedDeviceIds,
    error,
    success,
    copied,
    isUnlinking,
    isLoading,
    isAtLimit,
    canLink,
    handleGenerateCode,
    handleLinkDevices,
    handleCopyCode,
    handleUnlinkDevice,
    handleLinkCodeInputChange,
    refreshLinkedDevices: () => deviceId && loadLinkedDevices(deviceId),
  };
}
